import { PagedList } from "./paged-list"
import type {
  CollectionCompleteness,
  CollectionSource,
  CollectionSorting,
  CollectionItemState,
  CompletableCollection,
  QueryableCollection,
} from "./collection-source"

export type RemoteCollectionItemState = CollectionItemState
export type RemoteCollectionSorting<TSortField extends string = string> = CollectionSorting<TSortField>

/** Filters use plain serializable values; convert dates with toISOString() in the feature. */
export type RemoteCollectionQuery<T> = T extends Date
  ? never
  : T extends (...args: never[]) => unknown
    ? never
    : T extends object
      ? { [K in keyof T]: RemoteCollectionQuery<T[K]> }
      : T extends string | number | boolean | null | undefined | void
        ? T
        : never

export type RemoteCollectionQueryConstraint<T> = [T] extends [RemoteCollectionQuery<T>]
  ? unknown
  : { readonly queryMustBePlainData: never }

/** A fetch can coalesce adjacent logical pages. Return up to fetchSize items. */
export interface RemoteCollectionPageRequest {
  pageIndex: number
  pageSize: number
  logicalPageSize: number
  pageCount: number
  fetchSize: number
  direction: "initial" | "forward" | "backward"
  after?: string
  before?: string
  skip: number
  includeTotalCount: boolean
  signal: AbortSignal
}

export interface RemoteCollectionPageResult<TItem> {
  items: readonly TItem[]
  pageInfo?: { startCursor?: string | null; endCursor?: string | null }
  totalCount?: number
  hasNextPage?: boolean
  hasPreviousPage?: boolean
}

export interface RemoteCollectionPagingOptions {
  pageSize?: number
  includeTotalCount?: boolean
  prefetch?: { ahead?: number; behind?: number }
  maxConcurrentRequests?: number
}

/** Lets consumers load the whole result set into memory; see CompletableCollection. */
export interface RemoteCollectionCompleteOptions {
  /** Most records the collection loads in full. A larger result set reports "too-large". Default 20,000. */
  maxItems?: number
  /** Records per request while completing; the backend may return fewer. Default: the larger of 1,000 and pageSize. */
  fetchSize?: number
}

export interface RemoteCollectionOptions<
  TItem,
  TQuery,
  TSortField extends string = string,
> extends RemoteCollectionPagingOptions {
  initialQuery: TQuery
  getKey: (item: TItem) => string | number
  fetchPage: (
    request: RemoteCollectionPageRequest & {
      readonly query: Readonly<TQuery>
      readonly sorting: RemoteCollectionSorting<TSortField>
    }
  ) => Promise<RemoteCollectionPageResult<TItem>>
  /** Only these field ids may be sent to the backend for sorting. */
  sortableFields?: readonly TSortField[]
  initialSorting?: RemoteCollectionSorting<TSortField>
  /** Backends opt into multiple sort fields explicitly. Default: one. */
  maxSortFields?: number
  selectionOnQueryChange?: "clear" | "preserve"
  /** Lets consumers load the whole result set into memory. Without it, the collection never loads in full. */
  complete?: RemoteCollectionCompleteOptions
}

export interface RemoteCollection<TItem, TQuery = void, TSortField extends string = string>
  extends CollectionSource<TItem, TSortField>, QueryableCollection<TQuery>, Partial<CompletableCollection<TItem>> {
  /** Undefined unless the collection was created with `complete`; then see CompletableCollection. */
  readonly completeness?: CollectionCompleteness
  /** Without `complete`, a no-op that returns a no-op release. */
  requireComplete(): () => void
  /** Without `complete`, always undefined. */
  getAllItems(): readonly TItem[] | undefined
  /** Queries are transported and cached as plain data; see RemoteCollectionQuery. */
  applyQuery<TNext extends TQuery>(
    query:
      | (TNext & RemoteCollectionQueryConstraint<NoInfer<TNext>>)
      | ((previous: Readonly<TQuery>) => TNext & RemoteCollectionQueryConstraint<NoInfer<TNext>>)
  ): void
  /** Cached selected records, including stale pages; never loads or scans remote results. */
  getLoadedSelectedItems(): readonly TItem[]
  loadRange(start: number, end: number): Promise<void>
  /** Refetch viewport + prefetch in place; other cached pages reload on demand, or all of them while complete. */
  refresh(): Promise<void>
  /** Clear results and reset viewport to the first page. */
  reset(): Promise<void>
  refreshWhere(predicate: (item: TItem) => boolean): Promise<number>
  replaceItems(items: readonly TItem[]): number
  cancelPendingRequests(): void
  /** Inactive collections retain cached data but do not start requests. */
  setActive(active: boolean): void
  dispose(): void
}

/** Hook owns activity and disposal. Consumers cannot end its lifecycle. */
export type RemoteCollectionHandle<TItem, TQuery = void, TSortField extends string = string> = Omit<
  RemoteCollection<TItem, TQuery, TSortField>,
  "setActive" | "dispose" | "cancelPendingRequests"
>

/** Internal hook facade: expose collection commands, retain lifecycle authority. */
export function createRemoteCollectionHandle<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollection<TItem, TQuery, TSortField>
): RemoteCollectionHandle<TItem, TQuery, TSortField> {
  const handle = {} as RemoteCollectionHandle<TItem, TQuery, TSortField>
  const members = {
    query: true,
    sorting: true,
    selectedKeys: true,
    capabilities: true,
    pageSize: true,
    status: true,
    error: true,
    revision: true,
    dataRevision: true,
    resultSetRevision: true,
    itemCount: true,
    totalCount: true,
    loadedCount: true,
    isLoading: true,
    completeness: true,
    requireComplete: true,
    getAllItems: true,
    get: true,
    getLoadedEntries: true,
    getLoadedSelectedItems: true,
    getKey: true,
    getItemState: true,
    getItemError: true,
    getItemErrorRange: true,
    applyQuery: true,
    setSorting: true,
    setSelectedKeys: true,
    ensureRange: true,
    loadRange: true,
    retryRange: true,
    refresh: true,
    reset: true,
    refreshWhere: true,
    replaceItems: true,
    subscribe: true,
    subscribeData: true,
  } satisfies Record<keyof RemoteCollectionHandle<TItem, TQuery, TSortField>, true>
  for (const key of Object.keys(members) as (keyof typeof members)[]) {
    const value = collection[key]
    Object.defineProperty(
      handle,
      key,
      typeof value === "function"
        ? { enumerable: true, value: value.bind(collection) }
        : { enumerable: true, get: () => collection[key] }
    )
  }
  return Object.freeze(handle)
}

/** Query snapshots are plain data. This also supplies property-order independent equality. */
export function snapshotCollectionQuery<T>(value: T): T {
  const ancestors = new Set<object>()
  function visit(input: unknown): unknown {
    if (input === null || input === undefined || typeof input === "string" || typeof input === "boolean") return input
    if (typeof input === "number" && Number.isFinite(input)) return input
    if (typeof input !== "object" || input === null || ancestors.has(input)) {
      throw new Error("Remote collection queries must contain finite, non-circular plain data.")
    }
    ancestors.add(input)
    try {
      if (Array.isArray(input)) return Object.freeze(input.map(visit))
      if (Object.getPrototypeOf(input) !== Object.prototype && Object.getPrototypeOf(input) !== null) {
        throw new Error("Remote collection queries must contain finite, non-circular plain data.")
      }
      return Object.freeze(
        Object.fromEntries(
          Object.keys(input)
            .sort()
            .flatMap((key) => {
              const entry = (input as Record<string, unknown>)[key]
              return entry === undefined ? [] : [[key, visit(entry)]]
            })
        )
      )
    } finally {
      ancestors.delete(input)
    }
  }
  return visit(value) as T
}

export function collectionQueriesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right)
}

const DEFAULT_COMPLETE_MAX_ITEMS = 20_000
const DEFAULT_COMPLETE_FETCH_SIZE = 1_000
const PARTIAL: CollectionCompleteness = Object.freeze({ state: "partial" })
const COMPLETE: CollectionCompleteness = Object.freeze({ state: "complete" })
const releaseNothing = () => {}

/** The completion lane: loading, stopped at the size limit or at an error, or not running. */
type CompletionLane =
  | { readonly state: "idle" }
  | { readonly state: "loading"; readonly controller: AbortController }
  | { readonly state: "too-large" }
  | { readonly state: "error"; readonly error: unknown }

const IDLE_LANE: CompletionLane = Object.freeze({ state: "idle" })

function sameCompleteness(left: CollectionCompleteness, right: CollectionCompleteness) {
  if (left.state !== right.state) return false
  switch (left.state) {
    case "loading":
      return right.state === "loading" && left.loaded === right.loaded && left.total === right.total
    case "too-large":
      return right.state === "too-large" && left.total === right.total && left.limit === right.limit
    case "error":
      return (
        right.state === "error" &&
        left.error === right.error &&
        left.loaded === right.loaded &&
        left.total === right.total
      )
    default:
      return true
  }
}

class RemoteCollectionController<TItem, TQuery, TSortField extends string> implements RemoteCollection<
  TItem,
  TQuery,
  TSortField
> {
  private readonly list: PagedList<TItem>
  private readonly listeners = new Set<() => void>()
  private readonly dataListeners = new Set<() => void>()
  private readonly unsubscribeList: () => void
  private queryValue: TQuery
  private sortingValue: RemoteCollectionSorting<TSortField>
  private selectedKeysValue: readonly string[] = Object.freeze([])
  private revisionValue = 0
  private resultSetRevisionValue = 0
  private queryIdentity: string | undefined
  private lastRange: { start: number; end: number } | undefined
  private active = true
  private disposed = false
  private readonly completeOptions: { readonly maxItems: number; readonly fetchSize: number } | undefined
  private demands = 0
  private lane: CompletionLane = IDLE_LANE
  private completenessValue: CollectionCompleteness = PARTIAL
  private allItems: { readonly dataRevision: number; readonly items: readonly TItem[] } | undefined
  readonly capabilities: { readonly sorting: readonly TSortField[]; readonly maxSortFields: number }

  constructor(private readonly options: RemoteCollectionOptions<TItem, TQuery, TSortField>) {
    this.queryValue = snapshotCollectionQuery(options.initialQuery)
    this.queryIdentity = JSON.stringify(this.queryValue)
    const fields = options.sortableFields ?? []
    if (fields.some((field) => !field) || new Set(fields).size !== fields.length) {
      throw new Error("Remote collection sortableFields must contain distinct non-empty ids.")
    }
    const maxSortFields = options.maxSortFields ?? 1
    if (!Number.isInteger(maxSortFields) || maxSortFields < 1)
      throw new Error("Remote collection maxSortFields must be a positive integer.")
    this.capabilities = Object.freeze({ sorting: Object.freeze([...fields]), maxSortFields })
    this.sortingValue = this.validateSorting(options.initialSorting ?? [])
    if (options.complete) {
      const maxItems = options.complete.maxItems ?? DEFAULT_COMPLETE_MAX_ITEMS
      if (!Number.isInteger(maxItems) || maxItems < 0)
        throw new Error("Remote collection complete.maxItems must be a non-negative integer.")
      const fetchSize = options.complete.fetchSize ?? Math.max(DEFAULT_COMPLETE_FETCH_SIZE, options.pageSize ?? 0)
      if (!Number.isInteger(fetchSize) || fetchSize < 1)
        throw new Error("Remote collection complete.fetchSize must be a positive integer.")
      this.completeOptions = Object.freeze({ maxItems, fetchSize })
    }
    this.list = new PagedList({
      pageSize: options.pageSize,
      includeTotalCount: options.includeTotalCount,
      prefetch: options.prefetch,
      maxConcurrentRequests: options.maxConcurrentRequests,
      fetchPage: (request) => options.fetchPage({ ...request, query: this.queryValue, sorting: this.sortingValue }),
    })
    let dataRevision = this.list.dataRevision
    this.unsubscribeList = this.list.subscribe(() => {
      const dataChanged = dataRevision !== this.list.dataRevision
      dataRevision = this.list.dataRevision
      this.emit(dataChanged)
    })
  }

  get query() {
    return this.queryValue as Readonly<TQuery>
  }
  get sorting() {
    return this.sortingValue
  }
  get selectedKeys() {
    return this.selectedKeysValue
  }
  get revision() {
    return this.revisionValue
  }
  get dataRevision() {
    return this.list.dataRevision
  }
  get resultSetRevision() {
    return this.resultSetRevisionValue
  }
  get itemCount() {
    return this.list.itemCount
  }
  get totalCount() {
    return this.list.totalCount
  }
  get loadedCount() {
    return this.list.loadedCount
  }
  get isLoading() {
    return this.list.isLoading
  }
  get pageSize() {
    return this.list.pageSize
  }
  get completeness(): CollectionCompleteness | undefined {
    const options = this.completeOptions
    if (!options) return undefined
    const lane = this.lane
    const next: CollectionCompleteness = this.list.isComplete
      ? COMPLETE
      : lane.state === "loading"
        ? { state: "loading", loaded: this.list.loadedCount, total: this.list.totalCount }
        : lane.state === "too-large"
          ? { state: "too-large", total: this.list.totalCount, limit: options.maxItems }
          : lane.state === "error"
            ? {
                state: "error",
                error: lane.error,
                loaded: this.list.loadedCount,
                total: this.list.totalCount,
                retry: () => this.retryCompletion(),
              }
            : PARTIAL
    // Consumers compare snapshots by identity; keep it while nothing they can see changed.
    if (!sameCompleteness(this.completenessValue, next)) this.completenessValue = Object.freeze(next)
    return this.completenessValue
  }
  requireComplete() {
    if (!this.completeOptions || this.disposed) return releaseNothing
    this.demands++
    this.startCompletion()
    let released = false
    return () => {
      if (released) return
      released = true
      this.demands--
      // The records stay. Without a consumer that needs them, the rest of the result set is not loaded.
      if (this.demands === 0 && this.lane.state !== "idle") this.stopCompletion()
    }
  }
  getAllItems() {
    if (!this.completeOptions || !this.list.isComplete) return undefined
    const dataRevision = this.list.dataRevision
    if (this.allItems?.dataRevision !== dataRevision) {
      const items: TItem[] = new Array(this.list.itemCount)
      for (let index = 0; index < items.length; index++) items[index] = this.list.get(index)!
      this.allItems = { dataRevision, items }
    }
    return this.allItems.items
  }
  get error() {
    return this.list.getPageError(0)
  }
  get status(): "loading" | "error" | "ready" {
    return this.error !== undefined ? "error" : this.list.hasLoadedPage(0) || this.loadedCount > 0 ? "ready" : "loading"
  }
  get(index: number) {
    return this.list.get(index)
  }
  getLoadedEntries() {
    return this.list.getLoadedEntries()
  }
  getLoadedSelectedItems() {
    const selected = new Set(this.selectedKeysValue)
    return [...this.getLoadedEntries()].flatMap(([, item]) => (selected.has(this.getKey(item)) ? [item] : []))
  }
  getKey(item: TItem) {
    const key = this.options.getKey(item)
    if (typeof key !== "string" && !(typeof key === "number" && Number.isFinite(key))) {
      throw new Error("Remote collection keys must be strings or finite numbers.")
    }
    return String(key)
  }
  getItemState(index: number) {
    return this.list.getItemState(index)
  }
  getItemError(index: number) {
    if (!Number.isInteger(index) || index < 0) return undefined
    return this.list.getPageError(Math.floor(index / this.list.pageSize))
  }

  hasPageError(predicate: (error: unknown) => boolean) {
    return this.list.hasPageError(predicate)
  }
  getItemErrorRange(index: number) {
    if (this.getItemState(index) !== "error") return undefined
    const start = Math.floor(index / this.pageSize) * this.pageSize
    return Object.freeze({ start, end: Math.max(start, Math.min(start + this.pageSize, this.itemCount) - 1) })
  }

  applyQuery<TNext extends TQuery>(
    query:
      | (TNext & RemoteCollectionQueryConstraint<NoInfer<TNext>>)
      | ((previous: Readonly<TQuery>) => TNext & RemoteCollectionQueryConstraint<NoInfer<TNext>>)
  ) {
    this.assertUsable()
    const next = snapshotCollectionQuery(
      typeof query === "function" ? (query as (previous: Readonly<TQuery>) => TNext)(this.query) : query
    )
    const identity = JSON.stringify(next)
    if (identity === this.queryIdentity) return
    this.queryValue = next
    this.queryIdentity = identity
    if (this.options.selectionOnQueryChange !== "preserve") this.selectedKeysValue = Object.freeze([])
    this.resetResults()
  }

  setSorting(sorting: RemoteCollectionSorting<TSortField>) {
    this.assertUsable()
    const next = this.validateSorting(sorting)
    if (collectionQueriesEqual(next, this.sortingValue)) return
    this.sortingValue = next
    this.resetResults()
  }

  setSelectedKeys(keys: readonly string[]) {
    this.assertUsable()
    const next = [...new Set(keys)]
    if (next.some((key) => typeof key !== "string"))
      throw new Error("Remote collection selection requires string keys.")
    if (collectionQueriesEqual(next, this.selectedKeysValue)) return
    this.selectedKeysValue = Object.freeze(next)
    this.emit(false)
  }

  ensureRange(start: number, end: number) {
    if (this.disposed) return
    this.lastRange = { start, end }
    if (this.active) this.list.ensureRange(start, end)
  }

  async loadRange(start: number, end: number) {
    this.assertUsable()
    this.lastRange = { start, end }
    if (this.active) await this.list.loadRange(start, end)
  }

  async retryRange(start: number, end: number) {
    this.assertUsable()
    this.lastRange = { start, end }
    if (!this.active) return
    // Normal range loading skips totalCount === 0. Retry the failed empty-page
    // refresh explicitly so a recovered backend can also report newly added rows.
    const retrying =
      this.totalCount === 0 && start === 0 && end >= 0 && this.getItemError(0) !== undefined
        ? this.list.refresh()
        : this.list.retryRange(start, end)
    // Retrying the page completion stopped at resumes completion.
    if (this.lane.state === "error") this.retryCompletion()
    await retrying
  }

  async refresh() {
    this.assertUsable()
    if (!this.active) return
    const refreshing = this.list.refresh()
    if (this.completeOptions && (this.demands > 0 || this.list.isComplete)) {
      // A complete result refreshes in full, so no page stays stale.
      if (this.lane.state === "error") this.lane = IDLE_LANE
      this.startCompletion(true)
    }
    try {
      await refreshing
    } finally {
      // The refreshed count may now fit.
      if (this.lane.state === "too-large" && this.demands > 0) {
        this.lane = IDLE_LANE
        this.startCompletion()
      }
    }
  }

  async reset() {
    this.assertUsable()
    this.resetResults(false)
    if (this.active && this.lastRange) await this.list.loadRange(this.lastRange.start, this.lastRange.end)
  }

  async refreshWhere(predicate: (item: TItem) => boolean) {
    this.assertUsable()
    return this.active ? this.list.refreshWhere(predicate) : 0
  }

  replaceItems(items: readonly TItem[]) {
    this.assertUsable()
    return this.list.replaceItems(items, (item) => this.getKey(item))
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  subscribeData(listener: () => void) {
    this.dataListeners.add(listener)
    return () => {
      this.dataListeners.delete(listener)
    }
  }

  cancelPendingRequests() {
    this.list.cancelPendingRequests()
  }

  setActive(active: boolean) {
    if (this.disposed || this.active === active) return
    this.active = active
    if (!active) {
      if (this.lane.state === "loading") this.stopCompletion()
      this.list.cancelPendingRequests()
    } else {
      if (this.lastRange) this.list.ensureRange(this.lastRange.start, this.lastRange.end)
      this.startCompletion()
    }
  }

  dispose() {
    if (this.disposed) return
    if (this.lane.state === "loading") this.lane.controller.abort()
    this.lane = IDLE_LANE
    this.disposed = true
    this.unsubscribeList()
    this.list.invalidate()
    this.list.dispose()
    this.selectedKeysValue = Object.freeze([])
    this.listeners.clear()
    this.dataListeners.clear()
  }

  private resetResults(load = true) {
    this.resultSetRevisionValue++
    this.lastRange = { start: 0, end: this.list.pageSize - 1 }
    // Reuse the scheduler so aborted, slow transports still occupy their slots
    // across query changes. invalidate rejects their late completions.
    if (this.lane.state === "loading") this.lane.controller.abort()
    this.lane = IDLE_LANE
    this.list.invalidate()
    if (load && this.active) this.list.ensureRange(this.lastRange.start, this.lastRange.end)
    // A held demand loads the new result set in full, after its first visible page.
    if (load) this.startCompletion()
  }

  /** Runs the completion lane while a demand is held, or for a refresh of a complete result when forced. */
  private startCompletion(force = false) {
    const options = this.completeOptions
    if (!options || this.disposed || !this.active || (this.demands === 0 && !force)) return
    if (this.lane.state !== "idle") return
    const controller = new AbortController()
    this.lane = { state: "loading", controller }
    this.emit(false)
    this.list.loadAll({ maxItems: options.maxItems, fetchSize: options.fetchSize, signal: controller.signal }).then(
      (result) => this.finishCompletion(controller, result === "complete" ? IDLE_LANE : { state: "too-large" }),
      (error: unknown) =>
        this.finishCompletion(
          controller,
          error instanceof Error && error.name === "AbortError" ? IDLE_LANE : { state: "error", error }
        )
    )
  }

  private finishCompletion(controller: AbortController, lane: CompletionLane) {
    // A newer lane, a new result set or a stop replaced this one.
    if (this.lane.state !== "loading" || this.lane.controller !== controller) return
    this.lane = lane
    this.emit(false)
  }

  private stopCompletion() {
    if (this.lane.state === "loading") this.lane.controller.abort()
    this.lane = IDLE_LANE
    this.emit(false)
  }

  private retryCompletion() {
    if (this.disposed || this.lane.state !== "error") return
    this.list.clearErrors()
    this.lane = IDLE_LANE
    this.startCompletion()
    if (this.lane.state === "idle") this.emit(false)
  }

  private validateSorting(sorting: RemoteCollectionSorting<TSortField>): RemoteCollectionSorting<TSortField> {
    if (sorting.length > this.capabilities.maxSortFields)
      throw new Error(`Remote collection supports at most ${this.capabilities.maxSortFields} sort fields.`)
    const seen = new Set<string>()
    return Object.freeze(
      sorting.map((sort) => {
        if (!this.capabilities.sorting.includes(sort.id) || seen.has(sort.id) || typeof sort.desc !== "boolean") {
          throw new Error(`Remote collection does not support sort field "${sort.id}" or duplicate sorting.`)
        }
        seen.add(sort.id)
        return Object.freeze({ id: sort.id, desc: sort.desc })
      })
    )
  }

  private emit(dataChanged: boolean) {
    this.revisionValue++
    for (const listener of this.listeners) listener()
    if (dataChanged) for (const listener of this.dataListeners) listener()
  }

  private assertUsable() {
    if (this.disposed) throw new Error("Remote collection has been disposed.")
  }
}

/** @internal Whether any page of a framework-created collection failed with an error matching the predicate. */
export function remoteCollectionHasPageError<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollection<TItem, TQuery, TSortField>,
  predicate: (error: unknown) => boolean
) {
  return collection instanceof RemoteCollectionController && collection.hasPageError(predicate)
}

export function createRemoteCollection<TItem, TQuery, TSortField extends string = string>(
  options: RemoteCollectionOptions<TItem, TQuery, TSortField> & RemoteCollectionQueryConstraint<NoInfer<TQuery>>
): RemoteCollection<TItem, TQuery, TSortField> {
  return new RemoteCollectionController<TItem, TQuery, TSortField>(options)
}
